import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    fetchBranchCodReceipts,
    setCodReceiptFilters,
    clearCodReceiptError,
    fetchCodRiders,
    fetchCodDeliverySheets,
    fetchCodConsignments,
    resetDeliverySheetData,
    resetConsignments,
    createBranchCodReceipt,
} from '../../../store/slices/branchCodReceiptsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { BranchOption, DeliverySheetOption, RiderOption } from '../types/branchFinance.types';

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString() : '—';

const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

const formatCurrency = (amount: number) =>
    `Rs. ${amount.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const BranchCodReceiptsTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const {
        list,
        loadingList,
        error,
        branches,
        filters,
        pagination,
        riders,
        ridersLoading,
        deliverySheets,
        deliverySheetsLoading,
        consignments,
        consignmentsLoading,
        submitting,
        formError,
    } = useAppSelector((state) => state.branchCodReceipts);

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(15);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedRider, setSelectedRider] = React.useState<RiderOption | null>(null);
    const [selectedDeliverySheet, setSelectedDeliverySheet] = React.useState<DeliverySheetOption | null>(null);
    const [selectedConsignments, setSelectedConsignments] = React.useState<number[]>([]);
    const [localFormError, setLocalFormError] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const isAdmin = user?.type === 'ADMIN';
    const totalCount = pagination?.total ?? 0;

    React.useEffect(() => {
        dispatch(
            fetchBranchCodReceipts({
                branchId: filters.branchId,
                riderId: filters.riderId,
                page: page + 1,
                perPage: rowsPerPage,
            })
        ).catch((err) => {
            console.error('Failed to fetch COD receipts:', err);
        });
    }, [dispatch, filters.branchId, filters.riderId, page, rowsPerPage]);

    React.useEffect(() => {
        if (drawerOpen) {
            dispatch(
                fetchCodRiders({
                    branchId: isAdmin ? filters.branchId : (user?.branch_id ?? undefined),
                })
            );
        }
    }, [dispatch, drawerOpen, filters.branchId, isAdmin, user?.branch_id]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleBranchFilterChange = (_: unknown, option: BranchOption | null) => {
        dispatch(setCodReceiptFilters({ branchId: option?.id }));
        setPage(0);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedRider(null);
        setSelectedDeliverySheet(null);
        setSelectedConsignments([]);
        setLocalFormError(null);
        dispatch(resetDeliverySheetData());
        dispatch(clearCodReceiptError());
    };

    const handleRiderChange = (_: unknown, option: RiderOption | null) => {
        setSelectedRider(option);
        setSelectedDeliverySheet(null);
        setSelectedConsignments([]);
        dispatch(resetDeliverySheetData());
        if (option) {
            dispatch(fetchCodDeliverySheets(option.id));
        }
    };

    const handleDeliverySheetChange = (_: unknown, option: DeliverySheetOption | null) => {
        setSelectedDeliverySheet(option);
        setSelectedConsignments([]);
        dispatch(resetConsignments());
        if (option) {
            dispatch(fetchCodConsignments(option.id));
        }
    };

    const handleConsignmentToggle = (consignmentId: number) => {
        setSelectedConsignments((prev) =>
            prev.includes(consignmentId) ? prev.filter((id) => id !== consignmentId) : [...prev, consignmentId]
        );
    };

    const handleToggleAllConsignments = () => {
        if (selectedConsignments.length === consignments.length) {
            setSelectedConsignments([]);
        } else {
            setSelectedConsignments(consignments.map((item) => item.id));
        }
    };

    const totalAmount = React.useMemo(
        () =>
            consignments
                .filter((item) => selectedConsignments.includes(item.id))
                .reduce((sum, item) => sum + item.cod_amount, 0),
        [consignments, selectedConsignments]
    );

    const handleSubmit = async () => {
        setLocalFormError(null);

        if (!selectedRider) {
            setLocalFormError('Please select a rider.');
            return;
        }

        if (!selectedDeliverySheet) {
            setLocalFormError('Please select a delivery sheet.');
            return;
        }

        if (selectedConsignments.length === 0) {
            setLocalFormError('Please select at least one consignment.');
            return;
        }

        const result = await dispatch(
            createBranchCodReceipt({
                riderId: selectedRider.id,
                deliverySheetId: selectedDeliverySheet.id,
                consignmentIds: selectedConsignments,
            })
        );

        if (createBranchCodReceipt.fulfilled.match(result)) {
            setSuccessMessage('COD receipt created successfully.');
            handleDrawerClose();
            setPage(0);
            dispatch(
                fetchBranchCodReceipts({
                    branchId: filters.branchId,
                    riderId: filters.riderId,
                    page: 1,
                    perPage: rowsPerPage,
                })
            );
        }
    };

    return (
        <>
            <Card>
                <CardHeader
                    title="COD Receipts"
                    action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}>
                            Add Receipt
                        </Button>
                    }
                />
                <CardContent>
                    <Stack spacing={2}>
                        {successMessage && (
                            <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                                {successMessage}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" onClose={() => dispatch(clearCodReceiptError())}>
                                {error}
                            </Alert>
                        )}

                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                            {isAdmin && (
                                <Box sx={{ minWidth: { xs: '100%', md: 300 } }}>
                                    <Autocomplete
                                        options={branches}
                                        getOptionLabel={(option) => option.name}
                                        value={
                                            branches.find((branch) => branch.id === filters.branchId) || null
                                        }
                                        onChange={handleBranchFilterChange}
                                        renderInput={(params) => <TextField {...params} label="Filter by branch" />}
                                    />
                                </Box>
                            )}
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Delivery Sheet</TableCell>
                                        <TableCell>Rider</TableCell>
                                        <TableCell>Branch</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Collected By</TableCell>
                                        <TableCell>Created At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(list || []).map((receipt) => (
                                        <TableRow key={receipt.id}>
                                            <TableCell>{receipt.id}</TableCell>
                                            <TableCell>{receipt.delivery_sheet?.unique_id ?? '—'}</TableCell>
                                            <TableCell>{receipt.rider?.name ?? '—'}</TableCell>
                                            <TableCell>{receipt.branch?.name ?? '—'}</TableCell>
                                            <TableCell align="right">{formatCurrency(receipt.amount)}</TableCell>
                                            <TableCell>
                                                {receipt.collector?.name ?? '—'}
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDateTime(receipt.collected_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{formatDateTime(receipt.created_at)}</TableCell>
                                        </TableRow>
                                    ))}

                                    {!loadingList && list.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography align="center" color="text.secondary">
                                                    No COD receipts found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {loadingList && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose}>
                <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6">New COD Receipt</Typography>
                        <IconButton onClick={handleDrawerClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Stack spacing={2}>
                        {(localFormError || formError) && (
                            <Alert
                                severity="error"
                                onClose={() => {
                                    setLocalFormError(null);
                                    dispatch(clearCodReceiptError());
                                }}
                            >
                                {localFormError || formError}
                            </Alert>
                        )}

                        <Autocomplete
                            options={riders}
                            loading={ridersLoading}
                            getOptionLabel={(option) =>
                                option.branch?.name ? `${option.name} (${option.branch.name})` : option.name
                            }
                            value={selectedRider}
                            onChange={handleRiderChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Rider"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {ridersLoading ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            options={deliverySheets}
                            loading={deliverySheetsLoading}
                            getOptionLabel={(option) =>
                                `${option.unique_id} — Remaining ${formatCurrency(option.remaining_amount)}`
                            }
                            value={selectedDeliverySheet}
                            onChange={handleDeliverySheetChange}
                            disabled={!selectedRider}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Delivery Sheet"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {deliverySheetsLoading ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Divider />

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1">Outstanding Consignments</Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={consignments.length > 0 && selectedConsignments.length === consignments.length}
                                        indeterminate={
                                            selectedConsignments.length > 0 &&
                                            selectedConsignments.length < consignments.length
                                        }
                                        onChange={handleToggleAllConsignments}
                                    />
                                }
                                label="Select All"
                            />
                        </Stack>

                        <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Consignment #</TableCell>
                                        <TableCell>Booking Date</TableCell>
                                        <TableCell align="right">COD</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {consignments.map((consignment) => (
                                        <TableRow key={consignment.id} hover>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedConsignments.includes(consignment.id)}
                                                    onChange={() => handleConsignmentToggle(consignment.id)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {consignment.consignment_no}
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {consignment.status?.name ?? '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{formatDate(consignment.booking_date)}</TableCell>
                                            <TableCell align="right">{formatCurrency(consignment.cod_amount)}</TableCell>
                                        </TableRow>
                                    ))}

                                    {consignmentsLoading && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <CircularProgress size={24} />
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!consignmentsLoading && consignments.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No consignments available.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1">Total COD Amount</Typography>
                            <Typography variant="h6">{formatCurrency(totalAmount)}</Typography>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            size="large"
                        >
                            {submitting ? 'Submitting...' : 'Submit Receipt'}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};

export default BranchCodReceiptsTab;



